import styled from "@emotion/styled";
import {JobLinksRenderer, TJobLinkItem} from "../../common/JobLinksRenderer";
import log from "loglevel";
import {Job, ManifestStop, RoutePlanningUnassignedDetail} from "../../../generated/graphql";
import {parseGhJobStopsId} from "../utils/RoutePlanning.utils";

export interface UnassignedJobsContentProps {
  details: RoutePlanningUnassignedDetail[];
  showDismissOption?: boolean;
  /** DP-3175: selected jobs for looking up jobNumber/orderId from the new Service ID format. */
  selectedJobs?: Job[];
  /** DP-3175: manifest stops for looking up jobNumber/orderId for manifest stop jobs. */
  manifestStopsMap?: Record<number, ManifestStop[]>;
}

const GH_REASON_BY_CODE: Record<number, string> = {
  0: "unknown reason",
  1: "cannot serve required skill",
  2: "cannot be visited within time window",
  3: "does not fit into any vehicle due to capacity",
  4: "cannot be assigned due to max distance constraint of vehicles",
  21: "could not be assigned due to relation constraint",
  22: "could not be assigned due to allowed vehicle constraint",
  23: "could not be assigned due to max-time-in-vehicle constraint",
  24: "driver does not need a break",
  25: "could not be assigned due to disallowed vehicle constraint",
  26: "could not be assigned due to max drive time constraint",
  27: "could not be assigned due to max job constraint",
  28: "could not be assigned due to max activity constraint",
  29: "could not be assigned due to group relation constraint",
  30: "could not be assigned due to driving time break",
  50: "underlying location cannot be accessed over road network by at least one vehicle",
  51: "location is isolated and cannot reach any other location over road network",
  101: "delivery(s) scheduled before the pickup/both(s). The system is unable to plan routes with this"
};

const UnassignedJobsContent = ({
  details,
  showDismissOption,
  selectedJobs,
  manifestStopsMap
}: UnassignedJobsContentProps) => {
  const jobsById = new Map(selectedJobs?.map((job) => [job.jobId, job]));

  // Build manifest job lookup: jobId → {jobNumber, orderId} from manifestStopsMap
  const manifestJobsById = new Map<number, {jobNumber: string; orderId: number}>();
  if (manifestStopsMap) {
    for (const stops of Object.values(manifestStopsMap)) {
      for (const stop of stops) {
        if (!manifestJobsById.has(stop.job.jobId)) {
          manifestJobsById.set(stop.job.jobId, {
            jobNumber: stop.job.jobNumber,
            orderId: stop.order.orderId
          });
        }
      }
    }
  }

  // Map GH codes to user-facing codes (e.g. relation constraint → time window)
  const mapReasonCode = (code: number): number => {
    if (code === 21 || code === 22) return 2;
    return code;
  };

  // Unrecognised ids are counted and shown, never silently skipped (DP-3237).
  let unidentifiedCount = 0;
  const groupedDetails = details.reduce((acc, detail) => {
    const reasonCode = mapReasonCode(detail.code ?? 0);
    const reasonText = GH_REASON_BY_CODE[reasonCode] || detail.reason || GH_REASON_BY_CODE[0];

    const jobId = parseGhJobStopsId(detail.id)?.jobId ?? null;
    if (jobId == null) {
      log.error(`Unassigned job detail id is malformed: ${detail.id}`);
      unidentifiedCount += 1;
      return acc;
    }

    if (!acc[reasonCode]) {
      acc[reasonCode] = {reason: reasonText, items: []};
    }
    const isExist = acc[reasonCode].items.some((item) => item.jobId === jobId);
    if (!isExist) {
      const job = jobsById.get(jobId);
      const manifestJob = manifestJobsById.get(jobId);
      acc[reasonCode].items.push({
        jobNumber: job?.jobNumber ?? manifestJob?.jobNumber ?? String(jobId),
        jobId,
        orderId: job?.order?.orderId ?? manifestJob?.orderId ?? 0
      });
    }
    if ([50, 51, 101].includes(reasonCode)) {
      acc[reasonCode].prefix = acc[reasonCode].items.length > 1 ? "have" : "has";
    }
    return acc;
  }, {} as Record<number, {reason: string; items: TJobLinkItem[]; prefix?: string}>);

  return (
    <Container>
      <List>
        {Object.entries(groupedDetails).map(([code, group]) => (
          <li key={code}>
            Job {JobLinksRenderer(group.items)} {group.prefix ? group.prefix : ""} {group.reason}.
          </li>
        ))}
      </List>
      {unidentifiedCount > 0 && (
        <div data-testid="unidentified-unassigned-count">
          {unidentifiedCount} other item{unidentifiedCount > 1 ? "s" : ""} could not be identified.
        </div>
      )}
      {showDismissOption
        ? "Would you like to remove the job(s) mentioned and create routes without them? These jobs will return to the unassigned job board."
        : "Please make the necessary adjustments and try again."}
    </Container>
  );
};

export default UnassignedJobsContent;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 16px;
  & > li:not(:last-of-type) {
    margin-bottom: 12px;
  }
`;
